// @flow

import React, { type Node } from 'react';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { IfFeature } from '@tectonics/prs/feature';
import styled from 'styled-components';
import { Separator } from '../../../commons/atoms';

import { CashTitle } from './cash-title';
import { MailWarning } from './mail-warning';
import { PaynetNumber } from './paynet-number';
import { PaynetBarcode } from './paynet-barcode';
import { PaynetTipsBox } from './paynet-tips-box';
import { DisplayDueDate } from './display-due-date';
import { SaveToPdfButton } from './save-to-pdf-button';
import { PaynetLocations } from './paynet-locations';
import { PrintInstructionsButton } from './print-instructions-button';
import { DisplayTotalAmount } from './display-total-amount';
import { PaynetInstructions } from './paynet-instructions';
import { ArgentineCashInstructions } from './argentine-cash-instructions';
import { ArgentineCashTipsBox } from './argentine-cash-tips-box';
import { ArgentineCashNumber } from './argentine-cash-number';
import { HelpCenterInfo } from './help-center-info';

import {
  DisplayEfectyDueDate,
  DisplayEfectyVoucherCode,
  DisplayEfectyInstructions,
} from './efecty';

import {
  PagosnetContentWrapper,
  PagosnetNumber,
  PagosnetLocations,
} from './pagosnet';

export function CashPageContent(): Node {
  const features = useAllFeatures();

  return (
    <Root data-test-id="page-pending-payment">
      <CashTitle />

      <IfFeature allFeatures={features} wanted="display_cash_email_warning">
        <MailWarning />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_argentine_cash_email_warning">
        <MailWarning />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_pagosnet_email_warning">
        <MailWarning />
      </IfFeature>

      <PaymentInfoWrapper>

        <IfFeature allFeatures={features} wanted="display_cash_total_value">
          <DisplayTotalAmount />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_cash_due_date">
          <DisplayDueDate />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_argentine_cash_total_value">
          <DisplayTotalAmount />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_argentine_cash_due_date">
          <DisplayDueDate />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_pagosnet_total_value">
          <DisplayTotalAmount />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_pagosnet_due_date">
          <DisplayDueDate />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_efecty_due_date">
          <DisplayEfectyDueDate />
        </IfFeature>

      </PaymentInfoWrapper>

      <Separator direction="vertical" pattern="dotted" size={5} />

      <IfFeature allFeatures={features} wanted="display_cash_instructions">
        <PaynetInstructions />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_argentine_cash_instructions">
        <ArgentineCashInstructions />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_efecty_voucher_code">
        <DisplayEfectyVoucherCode />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_pagosnet_number">
        <PagosnetContentWrapper>
          <PagosnetNumber />
        </PagosnetContentWrapper>
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_cash_barcode">
        <PaynetBarcode />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_argentine_cash_barcode">
        <PaynetBarcode />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_cash_number">
        <PaynetNumber />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_argentine_cash_number">
        <ArgentineCashNumber />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="action_cash_save_pdf">
        <SaveToPdfButton />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="action_argentine_cash_save_pdf">
        <SaveToPdfButton />
        <Separator direction="vertical" pattern="dotted" size={6} />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="action_pagosnet_save_pdf">
        <PagosnetContentWrapper>
          <SaveToPdfButton />
        </PagosnetContentWrapper>
      </IfFeature>

      <IfFeature allFeatures={features} wanted="action_pagosnet_print_instructions">
        <PagosnetContentWrapper>
          <PrintInstructionsButton />
        </PagosnetContentWrapper>
        <Separator direction="vertical" pattern="dotted" size={6} />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_efecty_instructions">
        <Separator direction="vertical" pattern="dotted" size={5} />
        <DisplayEfectyInstructions />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_paynet_payment_locations">
        <PaynetLocations />
        <Separator direction="vertical" pattern="dotted" size={6} />
      </IfFeature>


      <IfFeature allFeatures={features} wanted="display_cash_tips">
        <PaynetTipsBox />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_argentine_cash_tips">
        <ArgentineCashTipsBox />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_pagosnet_locations">
        <PagosnetLocations />
      </IfFeature>

      <IfFeature allFeatures={features} wanted="display_cash_help_center_info">
        <HelpCenterInfo />
      </IfFeature>
    </Root>
  );
}

const Root = styled.div``;

const PaymentInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f8f9;
`;
